import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Printer } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { useActiveTenant } from '../contexts/tenant'
import type { Product } from '@wsfull/shared'

type Lot = {
  id: string; tenantId: string; productId: string; lotNumber: string
  receivedAt: string; expiryDate: string | null; notes: string | null
  sku: string | null; productName: string | null; tenantName: string | null
}

export function LotsPage() {
  const tenantId = useActiveTenant()
  const [selectedProduct, setSelectedProduct] = useState('')
  const [printingId, setPrintingId] = useState<string | null>(null)

  const { data: products = [] } = useQuery<Product[]>({
    queryKey: ['products', tenantId],
    queryFn: async () => (await api.get('/products', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  const { data: lots = [], isLoading } = useQuery<Lot[]>({
    queryKey: ['lots', tenantId, selectedProduct],
    queryFn: async () => {
      if (!tenantId) return []
      const params: Record<string, string> = { tenantId }
      if (selectedProduct) params.productId = selectedProduct
      return (await api.get('/lots', { params })).data
    },
    enabled: !!tenantId,
  })

  async function handlePrint(lotId: string) {
    setPrintingId(lotId)
    try {
      await api.post(`/lots/${lotId}/print`, {})
    } catch {
      // printer may not be configured — fallback: open ZPL in new tab
      window.open(`/api/lots/${lotId}/label`, '_blank')
    } finally {
      setPrintingId(null)
    }
  }

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Lotes</h2>
          <p className="text-sm text-gray-500">Rastreabilidade FIFO por lote de produto</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <span className="text-sm font-semibold text-gray-900">Filtros</span>
        </CardHeader>
        <CardContent>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Produto</label>
            <select className="w-64 rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={selectedProduct} onChange={e => setSelectedProduct(e.target.value)}>
              <option value="">Todos os produtos</option>
              {products.filter(p => p.active).map(p => (
                <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : lots.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Nenhum lote registrado — lotes são criados no recebimento de mercadoria</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Lote</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">SKU</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Produto</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Entrada</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Validade</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Obs.</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {lots.map((lot, idx) => (
                    <tr key={lot.id} className={`hover:bg-gray-50 ${idx === 0 && !selectedProduct ? 'bg-blue-50' : ''}`}>
                      <td className="px-5 py-3 font-mono font-semibold text-gray-900">{lot.lotNumber}</td>
                      <td className="px-5 py-3 font-mono text-gray-700">{lot.sku ?? '—'}</td>
                      <td className="px-5 py-3 text-gray-700">{lot.productName ?? '—'}</td>
                      <td className="px-5 py-3 text-gray-600">{new Date(lot.receivedAt).toLocaleDateString('pt-BR')}</td>
                      <td className="px-5 py-3 text-gray-600">{lot.expiryDate ?? '—'}</td>
                      <td className="px-5 py-3 text-gray-500 text-xs">{lot.notes ?? '—'}</td>
                      <td className="px-5 py-3">
                        <Button variant="ghost" size="sm" onClick={() => handlePrint(lot.id)} loading={printingId === lot.id}>
                          <Printer className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!selectedProduct && lots.length > 0 && (
                <p className="px-5 py-2 text-xs text-blue-600 bg-blue-50 border-t border-blue-100">
                  Linha azul = lote mais antigo (próximo FIFO para transferência)
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
